import { parseDateSafe } from './dateHelper.js';

/**
 * True when a hire/employment date is missing or a MySQL zero date.
 * @param {string|Date|null|undefined} value
 * @returns {boolean}
 */
export function isBlankHireDate(value) {
  if (value == null || value === '') return true;
  if (value instanceof Date) return Number.isNaN(value.getTime());
  const raw = String(value).trim();
  return !raw || raw === '0000-00-00';
}

/**
 * First usable hire date from HR profile / account fallbacks.
 * @param {...(string|Date|null|undefined)} candidates
 * @returns {string|Date|''}
 */
export function pickHireDate(...candidates) {
  for (const value of candidates) {
    if (!isBlankHireDate(value)) return value;
  }
  return '';
}

/**
 * Format tenure between hire date and end (or today) as "X years Y months".
 * @param {string|Date|null|undefined} hireDate
 * @param {string|Date|null|undefined} [endDate] - termination date; defaults to today
 * @returns {string} empty string when hire date is missing/invalid
 */
export function formatTenure(hireDate, endDate = null) {
  const start = parseDateSafe(hireDate);
  if (!start) return '';

  let end = endDate != null && endDate !== '' ? parseDateSafe(endDate) : new Date();
  if (!end) end = new Date();

  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  if (startDay > endDay) return '0 years 0 months';

  let totalMonths =
    (endDay.getFullYear() - startDay.getFullYear()) * 12
    + (endDay.getMonth() - startDay.getMonth());
  if (endDay.getDate() < startDay.getDate()) totalMonths -= 1;
  if (totalMonths < 0) totalMonths = 0;

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (years === 0 && months === 0) return 'Less than a month';
  if (years === 0) return `${months} ${months === 1 ? 'month' : 'months'}`;
  if (months === 0) return `${years} ${years === 1 ? 'year' : 'years'}`;
  return `${years} ${years === 1 ? 'year' : 'years'} ${months} ${months === 1 ? 'month' : 'months'}`;
}

/**
 * Compact tenure for inline display next to hire date: "1 year", "2 years", or "3 months".
 * @param {string|Date|null|undefined} hireDate
 * @param {string|Date|null|undefined} [endDate]
 * @returns {string}
 */
export function formatTenureCompact(hireDate, endDate = null) {
  const start = parseDateSafe(hireDate);
  if (!start) return '';

  let end = endDate != null && endDate !== '' ? parseDateSafe(endDate) : new Date();
  if (!end) end = new Date();

  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  if (startDay > endDay) return '0 years';

  let totalMonths =
    (endDay.getFullYear() - startDay.getFullYear()) * 12
    + (endDay.getMonth() - startDay.getMonth());
  if (endDay.getDate() < startDay.getDate()) totalMonths -= 1;
  if (totalMonths < 0) totalMonths = 0;

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (years >= 1) {
    return `${years} year${years === 1 ? '' : 's'}`;
  }
  if (months >= 1) {
    return `${months} month${months === 1 ? '' : 's'}`;
  }
  return 'Less than a month';
}
